import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { Icon } from '../Icon';
import ToastContent from './ToastContent';

interface IToastContainerProps {
  appearance: 'error' | 'info' | 'success' | 'warning';
  children?: React.ReactNode;
}

const Layout = styled(motion.div)<Partial<IToastContainerProps>>`
  position: relative;
  flex-shrink: 0;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  height: ${toRem(66)};
  min-height: ${toRem(77)};
  margin-bottom: ${toRem(5.5)};
  padding: ${toRem(16.5)} ${toRem(22)};
  border-radius: ${toRem(5.5)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.dialog};
  overflow: visible;

  @media (min-width: ${theme.breakpoint.small}px) {
    width: auto;
    min-width: ${toRem(385)};
    max-width: ${toRem(450)};
  }

  &:last-child {
    margin-bottom: 0;
  }

  ${({ appearance }) =>
    appearance === 'success' &&
    css`
      border-left: ${toRem(5.5)} solid ${theme.color.success.medium};
    `}

  ${({ appearance }) =>
    appearance === 'error' &&
    css`
      border-left: ${toRem(5.5)} solid ${theme.color.danger.medium};
    `}

    ${({ appearance }) =>
    appearance === 'warning' &&
    css`
      border-left: ${toRem(5.5)} solid ${theme.color.warning.medium};
    `}
`;

const StyledIcon = styled(Icon).attrs({ width: 22, height: 22 })`
  margin-right: ${toRem(16.5)};
`;

const ToastContainer: React.FC<IToastContainerProps> = ({ appearance, children, ...props }) => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const animationDuration = theme.toast.timing * 0.95;

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, animationDuration);
      return () => clearTimeout(timer);
    }

    return () => null;
  });

  return (
    <AnimatePresence>
      {isVisible && (
        <Layout initial={{ opacity: 0, top: '-100%' }} animate={{ opacity: 1, top: 'auto' }} exit={{ opacity: 0 }} appearance={appearance} {...props}>
          {appearance === 'error' && <StyledIcon icon="Error" fill={theme.color.danger.medium} />}
          {appearance === 'success' && <StyledIcon icon="Success" fill={theme.color.success.medium} />}
          {appearance === 'warning' && <StyledIcon icon="Error" fill={theme.color.warning.medium} />}
          {children}
        </Layout>
      )}
    </AnimatePresence>
  );
};

export const SuccessToastContainer: React.FC<{
  title?: string;
  message: string;
  dataCy?: string;
}> = ({ title = 'Success', message, dataCy = 'success-toast' }) => (
  <ToastContainer appearance="success" data-cy={dataCy}>
    <ToastContent title={title} message={message} />
  </ToastContainer>
);

export const FailToastContainer: React.FC<{
  title?: string;
  message: string;
}> = ({ title = 'Fail', message }) => (
  <ToastContainer data-cy="fail-toast" appearance="error">
    <ToastContent title={title} message={message} />
  </ToastContainer>
);

export default ToastContainer;
